import cx from 'classnames';
import {difference, filter, includes, isEmpty, map, xor} from 'lodash';
import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {StepWizardChildProps} from 'react-step-wizard';

import {Checkbox, ConfirmModal, WizardFooter} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {User, UserSummary, WizardFooterProps} from 'types';
import {getUserName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SELECT_USERS_TO_KEEP;

type Props = {
    usersToRemove: string[];
    users: UserSummary[];
    maxUsers: number;
    localUser: User;
    wizardFooterProps: WizardFooterProps;
    onSubmit: (emails: string[]) => void;
} & Partial<StepWizardChildProps>;

export const SelectUsersToKeepStep: React.FC<Props> = ({
    maxUsers, usersToRemove: usersToRemoveProps, wizardFooterProps, users, localUser, nextStep, previousStep, onSubmit
}) => {
    const {t} = useTranslation();

    const userEmails = useMemo(() => map(users, (user) => user.email), [users]);
    const [userEmailsToKeep, setUserEmailsToKeep] = useState<string[]>(
        isEmpty(usersToRemoveProps) ? [localUser.email] : difference(userEmails, usersToRemoveProps));
    const userEmailsToRemove = useMemo(() => difference(userEmails, userEmailsToKeep), [userEmails, userEmailsToKeep]);
    const usersToRemove = useMemo(() => filter(users, (user) => includes(userEmailsToRemove, user.email)), [users, userEmailsToRemove]);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const handleUserToggle = useCallback((user: UserSummary) => {
        setUserEmailsToKeep((prev) => xor(prev, [user.email]));
    }, []);

    const openConfirmModal = useCallback(() => {
        setConfirmModalOpen(true);
    }, []);

    const getUserLabel = useCallback((user: UserSummary, isMe = false) => `${getUserName(t, user, isMe)} (${user.email})`, [t]);

    const handleRemoveUsers = useCallback((confirm: boolean) => {
        if (confirm) {
            onSubmit(userEmailsToRemove);
            nextStep();
        }
        setConfirmModalOpen(false);
    }, [userEmailsToRemove, onSubmit, nextStep]);

    return (
        <Fragment>
            <div className='tw-flex tw-flex-col tw-items-center'>
                <div>
                    <div className='tw-mb-8'>
                        <span>{t(T_KEYS.LABEL)}</span>
                        {` (${userEmailsToKeep.length} ${t(T_KEYS.OF)} ${maxUsers} ${t(T_KEYS.USERS_SELECTED)})`}
                    </div>
                    <div className='tw-max-h-52 tw-overflow-y-scroll tw-flex tw-flex-col'>
                        {map(users, (user) => {
                            const checked = includes(userEmailsToKeep, user.email);
                            const isMe = localUser.email === user.email;
                            return (
                                <div key={user.email} className={cx('tw-mb-4', {'tw-order-0': isMe})}>
                                    <Checkbox
                                        disabled={isMe || (userEmailsToKeep.length === maxUsers && !checked)}
                                        checked={checked}
                                        name={`user-to-remove-${user.eid}`}
                                        label={getUserLabel(user, isMe)}
                                        onChange={() => handleUserToggle(user)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <ConfirmModal
                title={t(T_KEYS.CONFIRM_MODAL.TITLE)}
                open={confirmModalOpen}
                maxWidth='sm'
                fullWidth
                onClose={handleRemoveUsers}
            >
                <div className='tw-ts-h5 tw-mb-4'>{t(T_KEYS.CONFIRM_MODAL.MESSAGE)}</div>
                {map(usersToRemove, (user, index) => (
                    <div key={user.email} className='tw-my-2 tw-ts-h5'>{`${index + 1}. ${getUserLabel(user)}`}</div>
                ))}
            </ConfirmModal>
            <WizardFooter
                {...wizardFooterProps}
                handlePrevious={previousStep}
                disabledNext={wizardFooterProps.disabledNext || userEmailsToKeep.length > maxUsers}
                handleNext={openConfirmModal}
            />
        </Fragment>
    );
};